<template>
  <main-layout
    :hasControlsBar="false"
    :breadcrumb="getBreadcrumb">
    <div class="container">

      <div class="controls__filters note">
        <grid-row col-spaced>
          <grid-col col-size="3">
            <label>{{ $t('health.NutritionistNotes.selectPatient') }}</label>
            <health-explorer-controls-user-select
              v-model="selectedPatientId"
              :options="patientOptions"
              v-on:click="loadNotes"
              v-show="patientOptions.length > 0"
            />
          </grid-col>
        </grid-row>
      </div>

      <grid-row column-align="center">
        <grid-col col-size="4">
          <h1 class="page-title">{{ $t('health.NutritionistNotes.title') }}</h1>
        </grid-col>
      </grid-row>

      <grid-row column-align="center" class="margin-top-xl">
        <grid-col col-size="4">
          <div v-if="items.length > 0" class="notes-list">
            <div v-for="item in items" :key="item.noteId" class="note-card">
              <div class="note-content">
                <p v-if="item.allergies"><strong>{{ $t('health.NutritionistNotes.allergies') }}:</strong> {{ item.allergies }}</p>
                <p v-if="item.intolerances"><strong>{{ $t('health.NutritionistNotes.intolerances') }}:</strong> {{ item.intolerances }}</p>
                <p v-if="item.preferences"><strong>{{ $t('health.NutritionistNotes.preferences') }}:</strong> {{ item.preferences }}</p>
                <p v-if="item.food_dislike"><strong>{{ $t('health.NutritionistNotes.food_dislike') }}:</strong> {{ item.food_dislike }}</p>
                <p v-if="item.supplements"><strong>{{ $t('health.NutritionistNotes.supplements') }}:</strong> {{ item.supplements }}</p>
                <p v-if="item.additional_comment"><strong>{{ $t('health.NutritionistNotes.additional_comment') }}:</strong> {{ item.additional_comment }}</p>
              </div>
              <div class="note-actions">
                <button class="btn btn-edit" @click="editNote(item)">
                  {{ $t('health.NutritionistNotes.edit') }}
                </button>
                <button class="btn btn-delete" @click="confirmDelete(item)">
                  {{ $t('health.NutritionistNotes.delete') }}
                </button>
              </div>
            </div>
          </div>
          <div v-else class="empty-state">
            {{ $t('health.NutritionistNotes.empty') }}
          </div>
        </grid-col>
      </grid-row>

      <grid-row column-align="center" class="margin-top-xl">
        <grid-col col-size="4">
          <div class="note-form">
            <h2 class="form-title">
              {{ isEditing ? $t('health.NutritionistNotes.titleEdit') : $t('health.NutritionistNotes.titleCreate') }}
            </h2>
            <div class="flex-direction-variable">
              <div class="form-group max-width">
                <label>{{ $t('health.NutritionistNotes.allergies') }}</label>
                <textarea v-model="form.allergies" rows="2" />
              </div>
              <div class="form-group max-width">
                <label>{{ $t('health.NutritionistNotes.intolerances') }}</label>
                <textarea v-model="form.intolerances" rows="2" />
              </div>
              <div class="form-group max-width">
                <label>{{ $t('health.NutritionistNotes.preferences') }}</label>
                <textarea v-model="form.preferences" rows="2" />
              </div>
              <div class="form-group max-width">
                <label>{{ $t('health.NutritionistNotes.food_dislike') }}</label>
                <textarea v-model="form.food_dislike" rows="2" />
              </div>
              <div class="form-group max-width">
                <label>{{ $t('health.NutritionistNotes.supplements') }}</label>
                <textarea v-model="form.supplements" rows="2" />
              </div>
              <div class="form-group max-width">
                <label>{{ $t('health.NutritionistNotes.additional_comment') }}</label>
                <textarea v-model="form.additional_comment" rows="3" />
              </div>
            </div>
            <div class="form-actions">
              <button class="btn btn-primary" @click="saveNote">
                {{ isEditing ? $t('health.NutritionistNotes.save') : $t('health.NutritionistNotes.create') }}
              </button>
              <button v-if="isEditing" class="btn btn-secondary" @click="cancelEdit">
                {{ $t('health.NutritionistNotes.cancel') }}
              </button>
            </div>
          </div>
        </grid-col>
      </grid-row>

      <div v-if="showDeleteModal" class="modal-overlay" @click.self="showDeleteModal = false">
        <div class="modal">
          <p>{{ $t('health.NutritionistNotes.deleteConfirm') }}</p>
          <div class="modal-actions">
            <button class="btn btn-delete" @click="deleteNote">
              {{ $t('health.NutritionistNotes.confirmDelete') }}
            </button>
            <button class="btn btn-secondary" @click="showDeleteModal = false">
              {{ $t('health.NutritionistNotes.cancel') }}
            </button>
          </div>
        </div>
      </div>

    </div>
  </main-layout>
</template>

<script>
import MainLayout from '@/layouts/MainLayout';
import GridRow from '@/components/shared/grid/GridRow';
import GridCol from '@/components/shared/grid/GridCol';
import nutritionistNotesApi from "../../api/nutritionistNotes";
import patientApi from "../../api/patient";
import HealthExplorerControlsUserSelect from "../../components/explorer/HealthExplorerControlsGroupSelect.vue";

const emptyForm = () => ({
  allergies: '',
  intolerances: '',
  preferences: '',
  food_dislike: '',
  supplements: '',
  additional_comment: ''
});

export default {
  name: 'NutritionistNotes',
  components: { HealthExplorerControlsUserSelect, MainLayout, GridRow, GridCol },
  data() {
    return {
      nutritionistNoteData: null,
      items: [],
      form: emptyForm(),
      isEditing: false,
      editingNoteId: null,
      showDeleteModal: false,
      noteToDelete: null,
      selectedPatientId: null,
      patientOptions: []
    };
  },
  computed: {
    getBreadcrumb() {
      return [];
    }
  },
  created() {
    patientApi.getPatients(this)
      .then(data => {
        this.patientOptions = data.map(p => ({ id: p.id, label: p.username }));
        if (this.patientOptions.length > 0) {
          this.selectedPatientId = this.patientOptions[0].id;
        }
      })
      .catch(err => console.error('Error fetching patients:', err));
  },
  methods: {
    loadNotes() {
      nutritionistNotesApi.getAllNotes(this, this.selectedPatientId)
        .then(data => { this.items = data; })
        .catch(error => console.error(error));
    },
    editNote(item) {
      this.isEditing = true;
      this.editingNoteId = item.noteId;
      this.form = {
        allergies: item.allergies || '',
        intolerances: item.intolerances || '',
        preferences: item.preferences || '',
        food_dislike: item.food_dislike || '',
        supplements: item.supplements || '',
        additional_comment: item.additional_comment || ''
      };
      this.$nextTick(() => {
        const formEl = this.$el.querySelector('.note-form');
        if (formEl) formEl.scrollIntoView({ behavior: 'smooth' });
      });
    },
    cancelEdit() {
      this.isEditing = false;
      this.editingNoteId = null;
      this.form = emptyForm();
    },
    saveNote() {
      if (this.isEditing) {
        nutritionistNotesApi.updateNote(this, this.editingNoteId, this.form)
          .then(() => { this.cancelEdit(); this.loadNotes(); })
          .catch(error => console.error(error));
      } else {
        nutritionistNotesApi.createNote(this, this.selectedPatientId, this.form)
          .then(() => { this.form = emptyForm(); this.loadNotes(); })
          .catch(error => console.error(error));
      }
    },
    confirmDelete(item) {
      this.noteToDelete = item;
      this.showDeleteModal = true;
    },
    deleteNote() {
      nutritionistNotesApi.removeNote(this, this.noteToDelete.noteId)
        .then(() => {
          this.showDeleteModal = false;
          this.noteToDelete = null;
          this.loadNotes();
        })
        .catch(error => console.error(error));
    }
  }
};
</script>

<style lang="scss" scoped>
@import "src/stylesheets/variables";

.data-container {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
}

.row {
  display: flex;
  margin-bottom: 20px;
}

.flex {
  display: flex;
}

.flex-direction-variable {
  display: flex;
  flex-wrap: wrap;
  gap: 24px;

  @media (min-width: $screen-wide) {
    flex-direction: row;
    gap: 32px;
  }
}

.max-width {
  width: 100%;
  flex: 1 1 48%;
}

.margin-top-xxl {
  margin-top: $sp-margin;
}


.page-title {
  font-size: 1.6rem;
  text-align: center;
  margin-bottom: $sp-margin;
}

.form-title {
  font-size: 1.2rem;
  margin-top: 15px;
  margin-bottom: 20px;
  text-align: center;
}

.chart-title {
  display: flex;
  font-size: 1.2rem;
  margin-top: 15px;
  margin-bottom: -25px;
  text-align: center;
  align-items: center;
  justify-content: center;
}


.notes-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.note-card {
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  padding: 20px 24px;
  transition: box-shadow 0.2s ease;

  &:hover {
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
  }

  .note-content {
    p {
      margin: 6px 0;
      font-size: 0.95rem;
      line-height: 1.5;

      strong {
        color: $primary;
      }
    }
  }

  .note-actions {
    display: flex;
    gap: 12px;
    margin-top: 16px;
    padding-top: 12px;
    border-top: 1px solid rgba(0, 0, 0, 0.06);
  }
}

.empty-state {
  text-align: center;
  color: rgba(0, 0, 0, 0.4);
  font-size: 0.95rem;
  padding: 40px 0;
}


.note-form {
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  padding: 24px 28px;
  margin-bottom: $sp-margin;

  .form-group {
    display: flex;
    flex-direction: column;
    gap: 6px;

    label {
      font-size: 0.85rem;
      font-weight: 600;
      color: rgba(0, 0, 0, 0.6);
      text-transform: uppercase;
      letter-spacing: 0.04em;
    }

    textarea {
      width: 100%;
      border: 1px solid rgba(0, 0, 0, 0.15);
      border-radius: 6px;
      padding: 10px 12px;
      font-size: 0.95rem;
      resize: vertical;
      transition: border-color 0.2s ease;
      box-sizing: border-box;

      &:focus {
        outline: none;
        border-color: $primary;
        box-shadow: 0 0 0 3px rgba($primary, 0.1);
      }
    }
  }

  .form-actions {
    display: flex;
    gap: 12px;
    margin-top: 24px;
    justify-content: flex-end;
  }
}


.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal {
  background: #fff;
  border-radius: 10px;
  padding: 32px 36px;
  max-width: 420px;
  width: 90%;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.18);
  text-align: center;

  p {
    font-size: 1rem;
    margin-bottom: 24px;
  }

  .modal-actions {
    display: flex;
    gap: 12px;
    justify-content: center;
  }
}
</style>



// WEBPACK FOOTER //
// src/brand/health/layouts/nutritionistNotes/NutritionistNotes.vue